import { useEffect, useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Badge, Card, CardBody, Table } from "reactstrap"
import { toast } from "react-toastify"

import PageWrapper from "components/Common/PageWrapper"
import { useKpiMetricDefinitions } from "hooks/useKpiMetricDefinitions"
import { updateKpiMetricDefinition } from "api/employeeKpiApi"

const KpiMetricWeights = () => {
  document.title = "KPI Metric Weights"

  const queryClient = useQueryClient()
  const { data, isLoading } = useKpiMetricDefinitions()
  const definitions = data?.data ?? []
  const [weights, setWeights] = useState({})

  useEffect(() => {
    if (data?.data?.length) {
      setWeights(Object.fromEntries(data.data.map(d => [d.id, d.weight])))
    }
  }, [data])

  const mutation = useMutation({
    mutationFn: ({ id, payload }) => updateKpiMetricDefinition(id, payload),
    onSuccess: () => {
      toast.success("Metric weight updated.")
      queryClient.invalidateQueries({ queryKey: ["kpi-metric-definitions"] })
    },
  })

  const saveWeight = definition => {
    mutation.mutate({
      id: definition.id,
      payload: { weight: Number(weights[definition.id]), is_active: definition.is_active },
    })
  }

  const toggleActive = definition => {
    mutation.mutate({
      id: definition.id,
      payload: { weight: Number(weights[definition.id] ?? definition.weight), is_active: !definition.is_active },
    })
  }

  const totalWeight = definitions
    .filter(d => d.is_active)
    .reduce((sum, d) => sum + Number(weights[d.id] ?? d.weight), 0)

  return (
    <PageWrapper title="KPI Metric Weights">
      <Card>
        <CardBody>
          <p className="text-muted small mb-3">
            Adjust each metric&apos;s weight. Active weights are re-normalized automatically per employee
            based on which metrics apply to them, so the total below is informational, not a hard cap.
          </p>

          {isLoading && <div className="text-center text-muted py-4">Loading metric definitions…</div>}

          {!isLoading && (
            <div style={{ overflowX: "auto" }}>
              <Table responsive bordered size="sm">
                <thead>
                  <tr>
                    <th>Module</th>
                    <th>Metric</th>
                    <th className="text-center" style={{ width: 120 }}>Weight</th>
                    <th className="text-center" style={{ width: 100 }}>Active</th>
                    <th style={{ width: 100 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {definitions.map(definition => (
                    <tr key={definition.id}>
                      <td><Badge color="secondary">{definition.module_slug}</Badge></td>
                      <td>
                        {definition.name}
                        <br />
                        <span className="text-muted small">{definition.description}</span>
                      </td>
                      <td className="text-center">
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step={0.5}
                          className="form-control form-control-sm text-center"
                          value={weights[definition.id] ?? definition.weight}
                          onChange={e =>
                            setWeights(prev => ({ ...prev, [definition.id]: e.target.value }))
                          }
                        />
                      </td>
                      <td className="text-center">
                        <div className="form-check form-switch d-flex justify-content-center">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={definition.is_active}
                            onChange={() => toggleActive(definition)}
                          />
                        </div>
                      </td>
                      <td className="text-center">
                        <button
                          className="btn btn-sm btn-primary"
                          disabled={mutation.isPending}
                          onClick={() => saveWeight(definition)}
                        >
                          Save
                        </button>
                      </td>
                    </tr>
                  ))}
                  {definitions.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center text-muted py-4">
                        No metric definitions found.
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={2} className="text-end fw-bold">Total active weight</td>
                    <td className="text-center fw-bold">{totalWeight}</td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>
    </PageWrapper>
  )
}

export default KpiMetricWeights
